import { useState, useEffect, useCallback } from "react";
import { GetErrorLogs, GetErrorStats, GetNotificationLogs } from "./apis/get";
import { DeleteCleanupLogs, DeleteErrorLog } from "./apis/delete";
import { ApiRequest } from "../services/api";
import LoaderService from "../services/LoaderService";
import toast from "react-hot-toast";
import { createPortal } from "react-dom";

const ErrorLogs = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartId, setDragStartId] = useState(null);
  const [hasDragged, setHasDragged] = useState(false);
  const [filters, setFilters] = useState({
    skip: 0,
    limit: 50,
    severity: "",
    source: "",
  });
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState("errors");
  const [notificationLogs, setNotificationLogs] = useState([]);
  const [notificationFilters, setNotificationFilters] = useState({
    user_id: "",
    notification_type: "",
    limit: 50,
    offset: 0,
  });
  const [selectedNotificationLog, setSelectedNotificationLog] = useState(null);

  const fetchLogs = useCallback(async () => {
    LoaderService.show();
    try {
      const response = await GetErrorLogs(filters);
      setLogs(Array.isArray(response) ? response : response?.data || []);
    } catch (error) {
      console.error("Failed to load logs:", error);
      toast.error("Failed to load error logs");
      setLogs([]);
    } finally {
      LoaderService.hide();
    }
  }, [filters]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await GetErrorStats();
      setStats(response);
    } catch (error) {
      console.error("Failed to load stats:", error);
    }
  }, []);

  const fetchNotificationLogs = useCallback(async () => {
    LoaderService.show();
    try {
      const response = await GetNotificationLogs(notificationFilters);
      setNotificationLogs(response?.logs || []);
    } catch (error) {
      console.error("Failed to load notification logs:", error);
      toast.error("Failed to load notification logs");
      setNotificationLogs([]);
    } finally {
      LoaderService.hide();
    }
  }, [notificationFilters]);

  useEffect(() => {
    if (activeTab === "errors") {
      fetchLogs();
      fetchStats();
    } else {
      fetchNotificationLogs();
    }
  }, [activeTab, fetchLogs, fetchStats, fetchNotificationLogs]);

  const handleCheckboxMouseDown = (logId) => {
    setIsDragging(true);
    setDragStartId(logId);
    setHasDragged(false);
  };

  const handleCheckboxMouseEnter = (logId) => {
    if (isDragging && dragStartId !== null) {
      setHasDragged(true);
      const startIndex = logs.findIndex(l => l.id === dragStartId);
      const currentIndex = logs.findIndex(l => l.id === logId);
      const start = Math.min(startIndex, currentIndex);
      const end = Math.max(startIndex, currentIndex);
      const rangeIds = logs.slice(start, end + 1).map(l => l.id);
      setSelectedIds(prev => [...new Set([...prev, ...rangeIds])]);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDragStartId(null);
    setHasDragged(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mouseup', handleMouseUp);
      return () => document.removeEventListener('mouseup', handleMouseUp);
    }
  }, [isDragging]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 'a') {
        e.preventDefault();
        setSelectedIds(logs.map(l => l.id));
      }
      if (e.key === 'Delete' && selectedIds.length > 0) {
        handleBulkDelete();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedIds, logs]);

  const handleBulkDelete = useCallback(async () => {
    if (selectedIds.length === 0) {
      toast.error("No logs selected");
      return;
    }
    if (!window.confirm(`Delete ${selectedIds.length} selected error logs?`)) return;

    LoaderService.show();
    try {
      await ApiRequest("POST", "admin/error-logs/bulk-delete", { ids: selectedIds });
      toast.success(`Deleted ${selectedIds.length} error logs`);
      setSelectedIds([]);
      fetchLogs();
      fetchStats();
    } catch (error) {
      console.error("Bulk delete failed:", error);
      toast.error("Failed to delete error logs");
    } finally {
      LoaderService.hide();
    }
  }, [selectedIds, fetchLogs, fetchStats]);

  const handleDeleteLog = async (logId, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this error log?")) return;

    LoaderService.show();
    try {
      await DeleteErrorLog(logId);
      toast.success("Error log deleted successfully");
      fetchLogs();
      fetchStats();
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete error log");
    } finally {
      LoaderService.hide();
    }
  };

  const handleCleanup = async () => {
    if (!window.confirm("Delete logs older than 30 days?")) return;

    LoaderService.show();
    try {
      const response = await DeleteCleanupLogs(30);
      toast.success(response.message || "Logs cleaned up successfully");
      fetchLogs();
      fetchStats();
    } catch (error) {
      console.error("Cleanup failed:", error);
      toast.error("Failed to cleanup logs");
    } finally {
      LoaderService.hide();
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case "Critical": return "bg-red-500/10 text-red-500 border-red-500/20";
      case "High": return "bg-orange-500/10 text-orange-500 border-orange-500/20";
      case "Moderate": return "bg-yellow-500/10 text-yellow-500 border-yellow-500/20";
      default: return "bg-slate-500/10 text-slate-500 border-slate-500/20";
    }
  };

  return (
    <div className="mt-12 space-y-6 animate-fade-in">
      {/* Tab Switcher */}
      <div className="flex bg-slate-900/50 p-1.5 rounded-2xl border border-slate-800 w-fit">
        <button
          onClick={() => setActiveTab("errors")}
          className={`px-6 py-2.5 rounded-xl text-sm font-black uppercase tracking-wider transition-all ${
            activeTab === "errors"
              ? "bg-blue-600 text-white shadow-lg shadow-blue-500/20"
              : "text-slate-400 hover:text-slate-200"
          }`}
        >
          Error Logs
        </button>
        <button
          onClick={() => setActiveTab("notifications")}
          className={`px-6 py-2.5 rounded-xl text-sm font-black uppercase tracking-wider transition-all ${
            activeTab === "notifications"
              ? "bg-emerald-600 text-white shadow-lg shadow-emerald-500/20"
              : "text-slate-400 hover:text-slate-200"
          }`}
        >
          Notification Logs
        </button>
      </div>

      {activeTab === "errors" ? (
        <>
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-slate-900/50 p-5 rounded-2xl border border-slate-800">
            <div className="text-slate-400 text-xs font-bold uppercase mb-2">Total Errors</div>
            <div className="text-3xl font-black text-white">{stats.total_errors}</div>
          </div>
          <div className="bg-red-500/10 p-5 rounded-2xl border border-red-500/20">
            <div className="text-red-400 text-xs font-bold uppercase mb-2">Critical</div>
            <div className="text-3xl font-black text-red-500">{stats.by_severity?.Critical || 0}</div>
          </div>
          <div className="bg-orange-500/10 p-5 rounded-2xl border border-orange-500/20">
            <div className="text-orange-400 text-xs font-bold uppercase mb-2">High</div>
            <div className="text-3xl font-black text-orange-500">{stats.by_severity?.High || 0}</div>
          </div>
          <div className="bg-blue-500/10 p-5 rounded-2xl border border-blue-500/20">
            <div className="text-blue-400 text-xs font-bold uppercase mb-2">Last 24h</div>
            <div className="text-3xl font-black text-blue-500">{stats.last_24h}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col lg:flex-row justify-between items-center gap-4 bg-slate-900/50 p-4 rounded-2xl border border-slate-800">
        <div className="flex gap-3 w-full lg:w-auto">
          <select
            value={filters.severity}
            onChange={(e) => setFilters({ ...filters, severity: e.target.value, skip: 0 })}
            className="px-4 py-2 bg-slate-950 border border-slate-700 text-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Severities</option>
            {stats?.by_severity && Object.keys(stats.by_severity).map((severity) => (
              <option key={severity} value={severity}>
                {severity} ({stats.by_severity[severity]})
              </option>
            ))}
          </select>

          <select
            value={filters.source}
            onChange={(e) => setFilters({ ...filters, source: e.target.value, skip: 0 })}
            className="px-4 py-2 bg-slate-950 border border-slate-700 text-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Sources</option>
            {stats?.by_source && Object.keys(stats.by_source).map((source) => (
              <option key={source} value={source}>
                {source} ({stats.by_source[source]})
              </option>
            ))}
          </select>
        </div>

        <div className="flex gap-2">
          {selectedIds.length > 0 && (
            <button
              onClick={handleBulkDelete}
              className="px-4 py-2 bg-red-600 text-white font-black text-xs rounded-lg hover:bg-red-700 transition-all uppercase"
            >
              Delete Selected ({selectedIds.length})
            </button>
          )}
          <button
            onClick={fetchLogs}
            className="px-4 py-2 bg-slate-800 text-slate-300 font-black text-xs rounded-lg hover:bg-slate-700 transition-all uppercase"
          >
            Refresh
          </button>
          <button
            onClick={handleCleanup}
            className="px-4 py-2 bg-red-600/10 text-red-500 font-black text-xs rounded-lg hover:bg-red-600/20 transition-all uppercase border border-red-600/20"
          >
            Cleanup Old Logs
          </button>
        </div>
      </div>

      {/* Logs Table */}
      <div className="rounded-3xl border border-slate-800 overflow-hidden shadow-2xl">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-900/80 backdrop-blur-md sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={logs.length > 0 && selectedIds.length === logs.length}
                    onChange={(e) => setSelectedIds(e.target.checked ? logs.map(l => l.id) : [])}
                    className="w-4 h-4 rounded border-slate-700 bg-slate-800 text-blue-500 focus:ring-2 focus:ring-blue-500"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-black text-slate-400 uppercase">ID</th>
                <th className="px-4 py-3 text-left text-xs font-black text-slate-400 uppercase">Severity</th>
                <th className="px-4 py-3 text-left text-xs font-black text-slate-400 uppercase">Error Type</th>
                <th className="px-4 py-3 text-left text-xs font-black text-slate-400 uppercase">Message</th>
                <th className="px-4 py-3 text-left text-xs font-black text-slate-400 uppercase">Source</th>
                <th className="px-4 py-3 text-left text-xs font-black text-slate-400 uppercase">Endpoint</th>
                <th className="px-4 py-3 text-left text-xs font-black text-slate-400 uppercase">Created At</th>
                <th className="px-4 py-3 text-center text-xs font-black text-slate-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-slate-950">
              {Array.isArray(logs) && logs.length > 0 ? (
                logs.map((log) => (
                  <tr
                    key={log.id}
                    onClick={() => setSelectedLog(log)}
                    className="border-t border-slate-800 hover:bg-slate-900/50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(log.id)}
                        onMouseDown={() => handleCheckboxMouseDown(log.id)}
                        onMouseEnter={() => handleCheckboxMouseEnter(log.id)}
                        onChange={(e) => {
                          if (!hasDragged) {
                            if (e.target.checked) {
                              setSelectedIds([...selectedIds, log.id]);
                            } else {
                              setSelectedIds(selectedIds.filter(id => id !== log.id));
                            }
                          }
                        }}
                        className="w-4 h-4 rounded border-slate-700 bg-slate-800 text-blue-500 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-slate-500">#{log.id}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-black uppercase border ${getSeverityColor(log.severity)}`}>
                        {log.severity}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-slate-300">{log.error_type}</td>
                    <td className="px-4 py-3 text-sm text-slate-400 max-w-md truncate">{log.error_message}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-slate-800 text-slate-400 text-xs font-bold rounded border border-slate-700">
                        {log.source}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-slate-500">{log.endpoint || "N/A"}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {new Date(log.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={(e) => handleDeleteLog(log.id, e)}
                        className="text-red-500 hover:text-red-400 transition-colors p-1.5 hover:bg-red-500/10 rounded-lg"
                        title="Delete log"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-slate-500">
                    No error logs found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedLog && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100000] flex items-center justify-center p-4" onClick={() => setSelectedLog(null)}>
          <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-slate-900 border-b border-slate-800 p-6 flex items-center justify-between">
              <h3 className="text-xl font-black text-white uppercase">Error Details #{selectedLog.id}</h3>
              <button onClick={() => setSelectedLog(null)} className="text-slate-400 hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1">Severity</div>
                  <span className={`px-3 py-1.5 rounded-lg text-sm font-black uppercase border inline-block ${getSeverityColor(selectedLog.severity)}`}>
                    {selectedLog.severity}
                  </span>
                </div>
                <div>
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1">Source</div>
                  <span className="px-3 py-1.5 bg-slate-800 text-slate-300 text-sm font-bold rounded-lg border border-slate-700 inline-block">
                    {selectedLog.source}
                  </span>
                </div>
              </div>

              <div>
                <div className="text-xs font-bold text-slate-500 uppercase mb-1">Error Type</div>
                <div className="text-white font-bold">{selectedLog.error_type}</div>
              </div>

              <div>
                <div className="text-xs font-bold text-slate-500 uppercase mb-1">Error Message</div>
                <div className="text-slate-300 bg-slate-950 p-3 rounded-lg border border-slate-800">{selectedLog.error_message}</div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1">Endpoint</div>
                  <div className="text-slate-300 font-mono text-sm">{selectedLog.endpoint || "N/A"}</div>
                </div>
                <div>
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1">Method</div>
                  <div className="text-slate-300 font-bold">{selectedLog.method || "N/A"}</div>
                </div>
              </div>

              {selectedLog.query_params && (
                <div>
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1">Query Parameters</div>
                  <pre className="text-xs text-blue-300 bg-slate-950 p-3 rounded-lg border border-slate-800 overflow-x-auto max-h-40">
                    {selectedLog.query_params}
                  </pre>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1">Error Code</div>
                  <div className="text-red-400 font-bold">{selectedLog.error_code || "N/A"}</div>
                </div>
                <div>
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1">IP Address</div>
                  <div className="text-slate-300 font-mono text-sm">{selectedLog.ip_address || "N/A"}</div>
                </div>
              </div>

              {selectedLog.user_id && (
                <div>
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1">User ID</div>
                  <div className="text-slate-300 font-mono">{selectedLog.user_id}</div>
                </div>
              )}

              {selectedLog.payload && (
                <div>
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1">Payload</div>
                  <pre className="text-xs text-slate-300 bg-slate-950 p-3 rounded-lg border border-slate-800 overflow-x-auto max-h-40">
                    {selectedLog.payload}
                  </pre>
                </div>
              )}

              {selectedLog.response && (
                <div>
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1">Response</div>
                  <pre className="text-xs text-slate-300 bg-slate-950 p-3 rounded-lg border border-slate-800 overflow-x-auto max-h-40">
                    {selectedLog.response}
                  </pre>
                </div>
              )}

              {selectedLog.headers && (
                <div>
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1">Headers</div>
                  <pre className="text-xs text-slate-400 bg-slate-950 p-3 rounded-lg border border-slate-800 overflow-x-auto max-h-40">
                    {selectedLog.headers}
                  </pre>
                </div>
              )}

              {selectedLog.stack_trace && (
                <div>
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1">Stack Trace</div>
                  <pre className="text-xs text-slate-400 bg-slate-950 p-3 rounded-lg border border-slate-800 overflow-x-auto max-h-60">
                    {selectedLog.stack_trace}
                  </pre>
                </div>
              )}

              <div>
                <div className="text-xs font-bold text-slate-500 uppercase mb-1">Created At</div>
                <div className="text-slate-300">{new Date(selectedLog.created_at).toLocaleString()}</div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
      ) : (
        <div className="space-y-6">
          {/* Notification Filters */}
          <div className="flex flex-col lg:flex-row justify-between items-center gap-4 bg-slate-900/50 p-4 rounded-2xl border border-slate-800">
            <div className="flex flex-wrap gap-3 w-full lg:w-auto">
              <input
                type="text"
                placeholder="User ID"
                value={notificationFilters.user_id}
                onChange={(e) => setNotificationFilters({ ...notificationFilters, user_id: e.target.value, offset: 0 })}
                className="px-4 py-2 bg-slate-950 border border-slate-700 text-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
              <select
                value={notificationFilters.notification_type}
                onChange={(e) => setNotificationFilters({ ...notificationFilters, notification_type: e.target.value, offset: 0 })}
                className="px-4 py-2 bg-slate-950 border border-slate-700 text-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="">All Types</option>
                {stats?.by_notification_type && Object.keys(stats.by_notification_type).map((type) => (
                  <option key={type} value={type}>
                    {type.replace(/_/g, " ")} ({stats.by_notification_type[type]})
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={fetchNotificationLogs}
              className="px-4 py-2 bg-slate-800 text-slate-300 font-black text-xs rounded-lg hover:bg-slate-700 transition-all uppercase"
            >
              Refresh
            </button>
          </div>

          {/* Notification Logs Table */}
          <div className="rounded-3xl border border-slate-800 overflow-hidden shadow-2xl">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900/80 backdrop-blur-md sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-black text-slate-400 uppercase">User ID</th>
                    <th className="px-4 py-3 text-left text-xs font-black text-slate-400 uppercase">Target User</th>
                    <th className="px-4 py-3 text-left text-xs font-black text-slate-400 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-black text-slate-400 uppercase">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-black text-slate-400 uppercase">Logs</th>
                    <th className="px-4 py-3 text-left text-xs font-black text-slate-400 uppercase">Created At</th>
                  </tr>
                </thead>
                <tbody className="bg-slate-950">
                  {notificationLogs.length > 0 ? (
                    notificationLogs.map((log, index) => (
                      <tr
                        key={index}
                        onClick={() => setSelectedNotificationLog(log)}
                        className="border-t border-slate-800 hover:bg-slate-900/50 cursor-pointer transition-colors"
                      >
                        <td className="px-4 py-3 text-sm font-mono text-slate-300">{log.user_id}</td>
                        <td className="px-4 py-3 text-sm font-mono text-slate-500">{log.target_user_id || "N/A"}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 bg-emerald-500/10 text-emerald-500 text-[10px] font-black uppercase rounded-lg border border-emerald-500/20">
                            {log.notification_type?.replace(/_/g, " ")}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm font-bold text-slate-300">{log.title}</td>
                        <td className="px-4 py-3">
                          <span className="text-[10px] font-mono text-blue-400 bg-blue-500/5 px-2 py-1 rounded border border-blue-500/10 max-w-[150px] truncate block">
                            {JSON.stringify(log.logs)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">
                          {new Date(log.created_at).toLocaleString()}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                        No notification logs found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal for Error Logs */}
      {selectedLog && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100000] flex items-center justify-center p-4" onClick={() => setSelectedLog(null)}>
          <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-slate-900 border-b border-slate-800 p-6 flex items-center justify-between">
              <h3 className="text-xl font-black text-white uppercase">Error Details #{selectedLog.id}</h3>
              <button onClick={() => setSelectedLog(null)} className="text-slate-400 hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1">Severity</div>
                  <span className={`px-3 py-1.5 rounded-lg text-sm font-black uppercase border inline-block ${getSeverityColor(selectedLog.severity)}`}>
                    {selectedLog.severity}
                  </span>
                </div>
                <div>
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1">Source</div>
                  <span className="px-3 py-1.5 bg-slate-800 text-slate-300 text-sm font-bold rounded-lg border border-slate-700 inline-block">
                    {selectedLog.source}
                  </span>
                </div>
              </div>

              <div>
                <div className="text-xs font-bold text-slate-500 uppercase mb-1">Error Type</div>
                <div className="text-white font-bold">{selectedLog.error_type}</div>
              </div>

              <div>
                <div className="text-xs font-bold text-slate-500 uppercase mb-1">Error Message</div>
                <div className="text-slate-300 bg-slate-950 p-3 rounded-lg border border-slate-800">{selectedLog.error_message}</div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1">Endpoint</div>
                  <div className="text-slate-300 font-mono text-sm">{selectedLog.endpoint || "N/A"}</div>
                </div>
                <div>
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1">Method</div>
                  <div className="text-slate-300 font-bold">{selectedLog.method || "N/A"}</div>
                </div>
              </div>

              {selectedLog.query_params && (
                <div>
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1">Query Parameters</div>
                  <pre className="text-xs text-blue-300 bg-slate-950 p-3 rounded-lg border border-slate-800 overflow-x-auto max-h-40">
                    {selectedLog.query_params}
                  </pre>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1">Error Code</div>
                  <div className="text-red-400 font-bold">{selectedLog.error_code || "N/A"}</div>
                </div>
                <div>
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1">IP Address</div>
                  <div className="text-slate-300 font-mono text-sm">{selectedLog.ip_address || "N/A"}</div>
                </div>
              </div>

              {selectedLog.user_id && (
                <div>
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1">User ID</div>
                  <div className="text-slate-300 font-mono">{selectedLog.user_id}</div>
                </div>
              )}

              {selectedLog.payload && (
                <div>
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1">Payload</div>
                  <pre className="text-xs text-slate-300 bg-slate-950 p-3 rounded-lg border border-slate-800 overflow-x-auto max-h-40">
                    {selectedLog.payload}
                  </pre>
                </div>
              )}

              {selectedLog.response && (
                <div>
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1">Response</div>
                  <pre className="text-xs text-slate-300 bg-slate-950 p-3 rounded-lg border border-slate-800 overflow-x-auto max-h-40">
                    {selectedLog.response}
                  </pre>
                </div>
              )}

              {selectedLog.headers && (
                <div>
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1">Headers</div>
                  <pre className="text-xs text-slate-400 bg-slate-950 p-3 rounded-lg border border-slate-800 overflow-x-auto max-h-40">
                    {selectedLog.headers}
                  </pre>
                </div>
              )}

              {selectedLog.stack_trace && (
                <div>
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1">Stack Trace</div>
                  <pre className="text-xs text-slate-400 bg-slate-950 p-3 rounded-lg border border-slate-800 overflow-x-auto max-h-60">
                    {selectedLog.stack_trace}
                  </pre>
                </div>
              )}

              <div>
                <div className="text-xs font-bold text-slate-500 uppercase mb-1">Created At</div>
                <div className="text-slate-300">{new Date(selectedLog.created_at).toLocaleString()}</div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Detail Modal for Notification Logs */}
      {selectedNotificationLog && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100000] flex items-center justify-center p-4" onClick={() => setSelectedNotificationLog(null)}>
          <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-slate-900 border-b border-slate-800 p-6 flex items-center justify-between">
              <h3 className="text-xl font-black text-white uppercase">Notification Details</h3>
              <button onClick={() => setSelectedNotificationLog(null)} className="text-slate-400 hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1">User ID</div>
                  <div className="text-slate-300 font-mono text-sm">{selectedNotificationLog.user_id}</div>
                </div>
                <div>
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1">Target User ID</div>
                  <div className="text-slate-300 font-mono text-sm">{selectedNotificationLog.target_user_id || "N/A"}</div>
                </div>
              </div>

              <div>
                <div className="text-xs font-bold text-slate-500 uppercase mb-1">Notification Type</div>
                <span className="px-3 py-1.5 bg-emerald-500/10 text-emerald-500 text-sm font-black uppercase rounded-lg border border-emerald-500/20 inline-block">
                  {selectedNotificationLog.notification_type?.replace(/_/g, " ")}
                </span>
              </div>

              <div>
                <div className="text-xs font-bold text-slate-500 uppercase mb-1">Title</div>
                <div className="text-white font-bold text-lg">{selectedNotificationLog.title}</div>
              </div>

              <div>
                <div className="text-xs font-bold text-slate-500 uppercase mb-1">Message</div>
                <div className="text-slate-300 bg-slate-950 p-4 rounded-xl border border-slate-800 leading-relaxed italic">
                  "{selectedNotificationLog.message}"
                </div>
              </div>

              <div>
                <div className="text-xs font-bold text-slate-500 uppercase mb-1">Technical Logs</div>
                <pre className="text-xs text-blue-300 bg-slate-950 p-4 rounded-xl border border-slate-800 overflow-x-auto shadow-inner">
                  {JSON.stringify(selectedNotificationLog.logs, null, 2)}
                </pre>
              </div>

              <div className="pt-4 border-t border-slate-800 flex justify-between items-center text-xs text-slate-500 font-bold uppercase">
                <span>Created At</span>
                <span>{new Date(selectedNotificationLog.created_at).toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ErrorLogs;
